import { all, call, cancel, delay, fork, put, select, spawn, take, takeLatest } from "typed-redux-saga";
import { Action, bulkConfigureProductsActions } from "./bulk-configure-products-store";
import { errorActions } from "src/components/error-viewer/error-store";
import { applicationActions } from "src/components/application-store";
import { AttributeService, ConfiguratorService } from "src/backend/openapi/v1";
import { RootState } from "src/store";
import { claimLatestWrite, runInTurn } from "src/utils/saga-utils";

// Reads the selection from state rather than the action, so the initial load and a post-write refresh
// hit the same endpoint with the same payload
export function* getProductTypeAttributes() {
  try {
    const request = yield* select((state: RootState) => state.pageReducer.bulkConfigureProductsReducer.attributesRequest);
    if (!request) return;

    const result = yield* call(AttributeService.postApiV1AttributesProductTypeAttributes, request);
    yield put(bulkConfigureProductsActions.getProductTypeAttributesSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(bulkConfigureProductsActions.getProductTypeAttributesFail());
  }
}

export function* watchGetProductTypeAttributes() {
  yield takeLatest([Action.GetProductTypeAttributesRequest, Action.RefreshProductTypeAttributesRequest], getProductTypeAttributes);
}

export function* updateAttribute(
  action: ReturnType<typeof bulkConfigureProductsActions.updateAttributeRequest>,
  isSuperseded: () => boolean = () => false
) {
  try {
    yield put(applicationActions.setGlobalLoadingBar(true));
    // The "1" suffix is the generator disambiguating this from the single-item overload
    yield* call(ConfiguratorService.putApiV1ConfiguratorSystemProductConfigurationsAttributes1, action.payload.data);

    // A newer edit to this attribute has already been made, so the re-read this would trigger would show a
    // state the user has moved on from and reset the field they are typing in. The newer write does its own
    // re-read when it lands. See the identical guard in configure-product-saga.ts.
    if (isSuperseded()) return;

    // The write is what makes a value uniform across the selection, so re-read to show it
    yield put(bulkConfigureProductsActions.refreshProductTypeAttributesRequest());
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.onError?.();
  } finally {
    yield put(applicationActions.setGlobalLoadingBar(false));
  }
}

// Debounced per attribute, as in configure-product-saga: the renderer fires on every keystroke, and a
// shared window would drop the earlier field's save. No item id — one write covers the whole set. Not per
// part either: a DimensionType write carries every part it has, so a later part's action already holds the
// earlier one's value, and the pair reaches the backend as one write that can be judged whole.
export function* watchUpdateAttribute() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed-redux-saga doesn't export Task
  const pendingTaskByKey = new Map<string, any>();

  while (true) {
    const action = yield* take<ReturnType<typeof bulkConfigureProductsActions.updateAttributeRequest>>(Action.UpdateAttributeRequest);

    const key = action.payload.data.attributeId;

    // Claimed at arrival, not when the write gets its turn — see configure-product-saga.ts.
    const isSuperseded = claimLatestWrite(key);

    if (!action.payload.shouldDebounce) {
      yield* spawn(runInTurn, key, updateAttribute, action, isSuperseded);
      continue;
    }

    const existingTask = pendingTaskByKey.get(key);
    if (existingTask) yield* cancel(existingTask);

    const task = yield* fork(function* () {
      yield* delay(1000);
      // Detached, so cancelling this task can only ever cancel the wait above — see the identical comment
      // in configure-product-saga.ts. The write itself is queued behind whatever is still on the wire for
      // this attribute, which is what keeps two saves off the same rows at once.
      yield* spawn(runInTurn, key, updateAttribute, action, isSuperseded);
    });
    pendingTaskByKey.set(key, task);
  }
}

export function* bulkConfigureProductsSaga() {
  yield all([fork(watchGetProductTypeAttributes), fork(watchUpdateAttribute)]);
}
